import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../hooks/useApi';
import { formatDateTime, formatMoney } from '../../utils/format';
import { filterRoster, ROSTER_FILTERS } from '../../utils/rateBands';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { RATE_BANDS_TOUR_ID, rateBandsTourSteps } from './rateBandsTour';

// Rate Bands: everyone's band with the live day rate behind it, who has none,
// and the history of every change. Click a row to open the person's panel,
// where the band can be assigned or changed in place; the result is reported
// in the panel, next to the control that was used.

const displayName = (p) => `${p.preferred_name || p.first_name} ${p.last_name}`;

// audit_trail stamps 'YYYY-MM-DD HH:MM:SS' in UTC with no zone marker.
const auditDate = (ts) => (ts ? formatDateTime(ts.replace(' ', 'T') + (ts.endsWith('Z') ? '' : 'Z')) : '-');

function BandTag({ band, onCard }) {
  if (!band) {
    return <span className="inline-flex px-1.5 py-px rounded-md text-[9px] font-semibold bg-amber-100 text-amber-700">No band</span>;
  }
  return (
    <span className={`inline-flex px-1.5 py-px rounded-md text-[9px] font-semibold ${onCard ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
      title={onCard ? undefined : 'Not on the active rate card'}>
      {band}
    </span>
  );
}

function PersonPanel({ person, bands, onClose, onAssigned }) {
  const [history, setHistory] = useState(null);
  const [choice, setChoice] = useState(person.rate_band || '');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null); // { kind: 'ok'|'error', text }

  const loadHistory = useCallback(() => {
    setHistory(null);
    api.get(`rate-bands/${person.id}/history`).then(setHistory).catch(() => setHistory([]));
  }, [person.id]);

  // A different person: fresh history, no leftover message.
  useEffect(() => { loadHistory(); setNote(null); }, [person.id, loadHistory]);
  // The band moved (saved here, or changed on the People form): follow it in
  // the picker, but leave the note alone, or the "is now on B2" message is
  // wiped in the same tick it is written.
  useEffect(() => { setChoice(person.rate_band || ''); }, [person.rate_band]);

  const chosen = bands.find(b => b.band_code === choice);
  const unchanged = (choice || '') === (person.rate_band || '');

  const save = async () => {
    if (!choice) { setNote({ kind: 'error', text: 'Choose a band to assign.' }); return; }
    setSaving(true); setNote(null);
    try {
      const updated = await api.put(`rate-bands/${person.id}`, { rate_band: choice });
      onAssigned(updated);
      setNote({ kind: 'ok', text: `${displayName(person)} is now on ${updated.rate_band}.` });
      loadHistory();
    } catch (e) {
      setNote({ kind: 'error', text: e.message || 'The band could not be saved.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside data-tour="person-panel" className="bg-white border border-gray-200 rounded p-3 w-full lg:w-[320px] lg:shrink-0 self-start">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-[12px] font-bold text-navy">{displayName(person)}</div>
          <div className="text-[9px] text-gray-500">{[person.designation, person.resource_type_name, person.team_name].filter(Boolean).join(' · ') || ' '}</div>
        </div>
        <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-[14px] leading-none">×</button>
      </div>

      <div className="border border-gray-200 rounded p-2 mb-3 bg-gray-50">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] uppercase font-bold text-gray-500">Current band</span>
          <BandTag band={person.rate_band} onCard={person.band_on_card} />
        </div>
        <div className="text-[9px] text-gray-500 mb-2">
          {person.rate_band
            ? (person.band_on_card ? `${formatMoney(person.day_rate, 0)} a day on the active rate card` : 'The active rate card has no rate for this band')
            : 'Time this person logs costs nothing against a SOW until a band is set.'}
        </div>
        <label className="block text-[9px] uppercase font-bold text-gray-500 mb-1" htmlFor="assign-band">Assign band</label>
        <div className="flex gap-1">
          <select id="assign-band" className="form-select" value={choice} onChange={e => { setChoice(e.target.value); setNote(null); }} disabled={saving}>
            <option value="">Choose a band</option>
            {bands.map(b => (
              <option key={b.band_code} value={b.band_code}>
                {b.band_code}{b.band_name ? ` · ${b.band_name}` : ''} · {formatMoney(b.day_rate, 0)}
              </option>
            ))}
          </select>
          <button onClick={save} disabled={saving || unchanged}
            className="px-2 py-1 rounded text-[10px] font-semibold bg-navy text-white disabled:opacity-40 whitespace-nowrap">
            {saving ? 'Saving…' : (person.rate_band ? 'Change band' : 'Assign band')}
          </button>
        </div>
        {chosen && !unchanged && (
          <div className="text-[9px] text-gray-500 mt-1">
            {person.rate_band ? `${person.rate_band} → ${chosen.band_code}` : `Sets ${chosen.band_code}`}, {formatMoney(chosen.day_rate, 0)} a day.
          </div>
        )}
        {note && (
          <div role="status" className={`text-[9px] mt-1 ${note.kind === 'ok' ? 'text-emerald-700' : 'text-red-600'}`}>{note.text}</div>
        )}
        {bands.length === 0 && (
          <div className="text-[9px] text-amber-700 mt-1">There is no active rate card, so no band can be assigned. Set one on Admin → Rate Cards.</div>
        )}
      </div>

      <div className="text-[9px] uppercase font-bold text-gray-500 mb-1">Band history</div>
      {history === null && <div className="text-[9px] text-gray-400">Loading…</div>}
      {history && history.length === 0 && (
        <div className="text-[9px] text-gray-500">No recorded changes. {person.rate_band ? 'The band predates the history.' : ''}</div>
      )}
      {history && history.length > 0 && (
        <ol className="divide-y divide-gray-100">
          {history.map(h => (
            <li key={h.id} className="py-1.5">
              <div className="flex items-center gap-1 text-[10px]">
                {h.old_value ? <BandTag band={h.old_value} onCard /> : <span className="text-[9px] text-gray-400">none</span>}
                <span className="text-gray-400">→</span>
                <BandTag band={h.new_value} onCard />
              </div>
              <div className="text-[9px] text-gray-500">{auditDate(h.timestamp)} · {h.changed_by}</div>
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

export default function RateBandsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    api.get('rate-bands').then(d => { setData(d); setError(null); }).catch(e => setError(e.message || 'Could not load rate bands.'));
  }, []);
  useEffect(load, [load]);

  const tour = useFeatureTour(RATE_BANDS_TOUR_ID, { enabled: !!data });

  const people = data?.people || [];
  const bands = data?.bands || [];
  const rows = useMemo(() => filterRoster(people, filter, search), [people, filter, search]);
  const selected = people.find(p => p.id === selectedId) || null;

  const onAssigned = (updated) => {
    setData(d => {
      const next = d.people.map(p => (p.id === updated.id ? updated : p));
      return { ...d, people: next, unassigned: next.filter(p => p.unassigned).length };
    });
  };

  return (
    <div className="p-4 fade-in">
      <div data-tour="page-header" className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h1 className="text-[15px] font-black text-navy flex items-center gap-2">
            Rate Bands
            <button onClick={tour.start} title="Replay the guided tour" aria-label="Replay the guided tour"
              className="w-4 h-4 rounded-full border border-gray-300 text-[9px] text-gray-500 hover:bg-gray-100 leading-none">?</button>
          </h1>
          <div className="text-[9px] text-gray-500 mt-0.5">
            Everyone's rate band, the day rate the active rate card gives it, and the history of every change.
            {data?.card ? ` Rates from ${data.card.name}.` : (data ? ' There is no active rate card.' : '')}
          </div>
        </div>
      </div>

      {error && <div className="text-[10px] text-red-600 mb-2">{error}</div>}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button data-tour="unassigned" onClick={() => setFilter(filter === 'unassigned' ? 'all' : 'unassigned')}
          className={`px-2 py-1 rounded border text-[10px] font-semibold flex items-center gap-1.5 ${data?.unassigned ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-gray-200 bg-white text-gray-600'} ${filter === 'unassigned' ? 'ring-2 ring-gold/40' : ''}`}>
          <span className="text-[13px] font-black leading-none">{data ? data.unassigned : '–'}</span>
          <span>{data?.unassigned === 1 ? 'person with no band' : 'people with no band'}</span>
        </button>
        <div data-tour="filters" className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
          {ROSTER_FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-2 py-1 text-[10px] ${filter === f.id ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <input className="form-input max-w-[220px]" placeholder="Search name, band or resource type" value={search} onChange={e => setSearch(e.target.value)} />
        <span className="text-[9px] text-gray-500 ml-auto">{rows.length} of {people.length}</span>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 items-start">
        <div data-tour="roster" className="bg-white border border-gray-200 rounded overflow-x-auto flex-1 min-w-0 w-full">
          <table className="w-full">
            <thead><tr>
              {['Person', 'Resource type', 'Band', 'Day rate', 'Last changed', 'By'].map(h => (
                <th key={h} className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {!data && <tr><td colSpan={6} className="px-2 py-3 text-[10px] text-gray-400">Loading…</td></tr>}
              {data && rows.length === 0 && (
                <tr><td colSpan={6} className="px-2 py-3 text-[10px] text-gray-500">
                  {filter === 'unassigned' && !search ? 'Everyone has a band.' : 'Nobody matches.'}
                </td></tr>
              )}
              {rows.map(p => (
                <tr key={p.id} onClick={() => setSelectedId(p.id)}
                  className={`cursor-pointer hover:bg-gold/5 ${selectedId === p.id ? 'bg-gold/10' : ''} ${p.unassigned ? 'bg-amber-50/40' : ''}`}>
                  <td className="px-2 py-1 text-[10px] font-semibold text-navy whitespace-nowrap">
                    {displayName(p)}
                    {p.designation && <div className="text-[8px] text-gray-400 font-normal">{p.designation}</div>}
                  </td>
                  <td className="px-2 py-1 text-[9px] text-gray-600">{p.resource_type_name || '-'}</td>
                  <td className="px-2 py-1"><BandTag band={p.rate_band} onCard={p.band_on_card} /></td>
                  <td className="px-2 py-1 text-[10px] tabular-nums">
                    {p.day_rate != null ? formatMoney(p.day_rate, 0) : <span className="text-gray-400">{p.rate_band ? 'Not on card' : '-'}</span>}
                  </td>
                  <td className="px-2 py-1 text-[9px] text-gray-500 whitespace-nowrap">{p.last_changed_at ? auditDate(p.last_changed_at) : <span className="text-gray-400">-</span>}</td>
                  <td className="px-2 py-1 text-[9px] text-gray-500 whitespace-nowrap">{p.last_changed_by || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selected && (
          <PersonPanel key={selected.id} person={selected} bands={bands} onClose={() => setSelectedId(null)} onAssigned={onAssigned} />
        )}
      </div>

      {tour.open && <FeatureTour steps={rateBandsTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}
    </div>
  );
}
